import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API, getSessionToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NoteEditor } from "@/components/NoteEditor";
import { SendDialog } from "@/components/SendDialog";
import {
  UploadCloud, Loader2, Download, Send, Save, CheckCircle2, RotateCcw, Plus, Layers,
} from "lucide-react";

export default function NewOrder() {
  const [run, setRun] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | uploading | done
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const fileRef = useRef(null);
  const addRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const extractFiles = useCallback(async (files) => {
    const pdfs = Array.from(files || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (!pdfs.length) {
      toast.error("Carica uno o più file PDF");
      return;
    }
    setPhase("uploading");
    const fd = new FormData();
    pdfs.forEach((f) => fd.append("files", f));
    try {
      const res = await api.post("/runs/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRun(res.data);
      setPhase("done");
      toast.success(`Estratte ${res.data.notes.length} consegne`);
    } catch (e) {
      setPhase("idle");
      toast.error(e.response?.data?.detail || "Errore durante l'estrazione");
    }
  }, []);

  const addFiles = async (files) => {
    const pdfs = Array.from(files || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (!pdfs.length) return;
    const fd = new FormData();
    pdfs.forEach((f) => fd.append("files", f));
    const t = toast.loading("Estrazione consegne aggiuntive...");
    try {
      const res = await api.post(`/runs/${run.id}/notes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRun(res.data);
      toast.success(`Aggiunte ${pdfs.length} consegne`, { id: t });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'estrazione", { id: t });
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    extractFiles(e.dataTransfer.files);
  };

  const updateNote = (idx, updated) =>
    setRun((r) => ({ ...r, notes: r.notes.map((n, i) => (i === idx ? updated : n)) }));

  const removeNote = (idx) =>
    setRun((r) => ({ ...r, notes: r.notes.filter((_, i) => i !== idx) }));

  const normalizeNotes = (notes) =>
    notes.map((n) => ({
      note_id: n.note_id || "",
      filename: n.filename || "",
      header: n.header,
      items: (n.items || []).map((i) => ({
        numero_riga: i.numero_riga === "" || i.numero_riga == null ? null : parseInt(i.numero_riga, 10),
        codice_articolo: i.codice_articolo || "",
        lotto: (i.lotto ?? "").toString(),
        pezzi: i.pezzi === "" || i.pezzi == null ? null : parseInt(i.pezzi, 10),
      })),
    }));

  const persist = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/runs/${run.id}`, { notes: normalizeNotes(run.notes) });
      setRun(res.data);
      toast.success("Modifiche salvate");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    if (!(await persist())) return;
    window.open(`${API}/runs/${run.id}/download?token=${getSessionToken()}`, "_blank");
  };

  const openSend = async () => {
    if (!run.notes.length) {
      toast.error("Nessuna consegna da inviare");
      return;
    }
    if (!(await persist())) return;
    setSendOpen(true);
  };

  const reset = () => {
    setRun(null);
    setPhase("idle");
  };

  const totalItems = (run?.notes || []).reduce((s, n) => s + (n.items?.length || 0), 0);

  if (phase !== "done") {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100">Carica Delivery Notes</h2>
          <p className="text-sm text-slate-500 mt-1">
            Carica uno o più PDF di consegna: verranno estratti automaticamente e uniti in un unico file MM.
          </p>
        </div>

        <div
          data-testid="pdf-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => phase === "idle" && fileRef.current?.click()}
          className={`relative rounded-2xl border-2 border-dashed p-14 text-center cursor-pointer transition-all ${
            dragging ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-400"
          }`}
        >
          <input
            ref={fileRef}
            data-testid="pdf-file-upload-input"
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            onChange={(e) => extractFiles(e.target.files)}
          />
          {phase === "uploading" ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
              <div>
                <p className="font-semibold text-slate-900 dark:text-slate-100">Estrazione dati con AI in corso...</p>
                <p className="text-sm text-slate-500 mt-1">Analisi delle Delivery Notes tramite Claude</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <div className="h-16 w-16 rounded-2xl bg-blue-50 dark:bg-blue-950/40 grid place-items-center">
                <UploadCloud className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-slate-100">
                  Trascina qui i PDF oppure <span className="text-blue-600">sfoglia</span>
                </p>
                <p className="text-sm text-slate-500 mt-1">Puoi selezionare più file contemporaneamente</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-blue-50 dark:bg-blue-950/40 grid place-items-center">
            <Layers className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">
                Run di importazione
              </h2>
              <StatusBadge status={run.status} />
            </div>
            <p className="text-sm text-slate-500">
              {run.notes.length} consegne · {totalItems} articoli totali
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={addRef} type="file" accept="application/pdf" multiple className="hidden" data-testid="add-pdf-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          <Button data-testid="add-pdf-button" variant="outline" onClick={() => addRef.current?.click()} className="gap-2">
            <Plus className="h-4 w-4" /> Aggiungi PDF
          </Button>
          <Button data-testid="reset-button" variant="ghost" onClick={reset} className="gap-2 text-slate-500">
            <RotateCcw className="h-4 w-4" /> Nuova
          </Button>
          <Button data-testid="save-run-button" variant="outline" onClick={persist} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </Button>
          <Button data-testid="download-target-xlsx-button" variant="outline" onClick={download} className="gap-2">
            <Download className="h-4 w-4" /> Scarica XLSX
          </Button>
          <Button data-testid="open-send-email-button" onClick={openSend} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Send className="h-4 w-4" /> Invia via Email
          </Button>
        </div>
      </div>

      {run.notes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-slate-500">
          Nessuna consegna nella run. Usa "Aggiungi PDF" per caricarne.
        </div>
      ) : (
        <div className="space-y-4">
          {run.notes.map((note, idx) => (
            <NoteEditor
              key={note.note_id || idx}
              note={note}
              index={idx}
              onChange={(updated) => updateNote(idx, updated)}
              onRemove={() => removeNote(idx)}
            />
          ))}
        </div>
      )}

      <SendDialog
        run={run}
        settings={settings}
        open={sendOpen}
        onOpenChange={setSendOpen}
        onSent={(res) => setRun((r) => ({ ...r, status: "sent", recipients: res.recipients, cc: res.cc }))}
      />
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    draft: { label: "Bozza", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
    sent: { label: "Inviato", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" },
    error: { label: "Errore", cls: "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" },
  };
  const s = map[status] || map.draft;
  return (
    <Badge className={`${s.cls} border-0 gap-1`}>
      {status === "sent" && <CheckCircle2 className="h-3 w-3" />}
      {s.label}
    </Badge>
  );
};
